import { t } from "@lingui/macro";
import { unique } from "radash";
import type { Accept, FileRejection } from "react-dropzone-esm";
import { formatBytes } from "../format/misc";

interface IRules {
  accept?: Accept;
  maxSize?: number;
  minSize?: number;
  maxFiles?: number;
}

export function getDropzoneErrorMessage(
  rejection: FileRejection,
  rules?: IRules,
) {
  return rejection.errors.map((error) => {
    switch (error.code) {
      case "file-invalid-type": {
        const formats = rules?.accept
          ? unique(
              Object.entries(rules.accept)
                .flatMap(([, value]) => value)
                .map((ext) => ext.trim().toLowerCase()),
            ).sort()
          : null;

        return formats && formats.length > 0
          ? t`The file ${rejection.file.name} is not a supported file. Accepted formats are ${formats.join(", ")}.`
          : t`The file ${rejection.file.name} is not a supported file.`;
      }

      case "file-too-large":
        return rules?.maxSize
          ? t`The file ${rejection.file.name} is too large. Maximum size is ${formatBytes(rules.maxSize)}.`
          : t`The file ${rejection.file.name} is too large.`;

      case "file-too-small":
        return rules?.minSize
          ? t`The file ${rejection.file.name} is too small. Minimum size is ${formatBytes(rules.minSize)}.`
          : t`The file ${rejection.file.name} is too small.`;

      case "too-many-files":
        return rules?.maxFiles
          ? t`You can only upload ${rules.maxFiles} files at a time.`
          : t`You can only upload one file at a time.`;

      default:
        return t`The file could not be uploaded.`;
    }
  });
}

export function getDropzoneErrorMessages(
  rejections: Array<FileRejection>,
  rules?: IRules,
) {
  return unique(
    rejections.flatMap((rejection) =>
      getDropzoneErrorMessage(rejection, rules),
    ),
  );
}
